"use client"

import { useState, useEffect, useRef } from "react"
import dynamic from "next/dynamic"
import Image from "next/image"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { bookingUrlForCountry } from "@/lib/booking"
import { cmsLocale } from "@/lib/cms"
import { EditableField } from "@/components/editor/editable-field"
import {
  MapPinIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@/components/ui/icons"
import { FaqSection } from "@/components/ui/faq-section"
import { cn } from "@/lib/utils"
import {
  ClinicAddressHours,
  ClinicLanguageNote,
  ClinicParking,
  ClinicServices,
  ClinicTransport,
  type ClinicDetailData,
} from "../clinic-sections"

const SingleLocationMap = dynamic(
  () =>
    import("@/components/ui/single-location-map").then((m) => ({
      default: m.SingleLocationMap,
    })),
  {
    ssr: false,
    loading: () => (
      <div
        className="w-full rounded-[20px] bg-neutral-100"
        style={{ height: 400 }}
      />
    ),
  }
)

// ─── Types ────────────────────────────────────────────────

interface Clinic {
  id: string
  clinic: string
  address: string
  type: "mri" | "blood"
  lat: number
  lng: number
  image: string | null
  detail?: ClinicDetailData
}

interface SuggestedClinic {
  id: string
  clinic: string
  clinicSlug: string
  citySlug: string
  city: string
  address: string
  type: "mri" | "blood"
  description: string | null
  image: string | null
}

interface ClinicDetailClientProps {
  city: string
  citySlug: string
  /**
   * Used as the FaqSection `currentPage` so per-clinic pages don't duplicate
   * the "locations"-tagged FAQ schema across every URL. Not used in the
   * visible UI.
   */
  clinicSlug: string
  clinic: Clinic
  suggestedClinics: SuggestedClinic[]
  initialFaqs?: {
    question: string
    answer: string
    slug?: string
    updatedAt?: string
    pages?: string[]
  }[]
}

// ─── Mobile sticky section nav ────────────────────────────
// TODO: Componentize and share with biomarker/test pages once the design settles.
// Kept local to the clinic detail page for now.

// Height of the floating navbar pill the dropdown needs to sit below when fixed.
const STICKY_TOP_OFFSET = 68

function MobileSectionNav({
  sections,
  clinicId,
}: {
  sections: { id: string; label: string }[]
  clinicId: string
}) {
  const [active, setActive] = useState(sections[0]?.id || "")
  const [open, setOpen] = useState(false)
  // `position: sticky` breaks inside page-shell's `<main overflow-x-clip>` on
  // mobile Safari, so we flip to `position: fixed` once the sentinel scrolls
  // under the navbar and reserve space with a spacer to avoid layout jump.
  const [stuck, setStuck] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const [barHeight, setBarHeight] = useState(0)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id.replace(`${clinicId}-`, "")
            setActive(id)
          }
        })
      },
      { rootMargin: "-100px 0px -60% 0px" }
    )
    sections.forEach((s) => {
      const el = document.getElementById(`${clinicId}-${s.id}`)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [sections, clinicId])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const check = () => {
      const startRect = sentinel.getBoundingClientRect()
      const pastStart = startRect.top < STICKY_TOP_OFFSET
      // Release stickiness once the "Other locations" section reaches the
      // navbar line so the floating bar doesn't overlap it.
      const endEl = document.querySelector<HTMLElement>("[data-mobile-nav-end]")
      const barH = barRef.current?.offsetHeight ?? 0
      const pastEnd = endEl
        ? endEl.getBoundingClientRect().top <= STICKY_TOP_OFFSET + barH
        : false
      setStuck(pastStart && !pastEnd)
    }
    check()
    window.addEventListener("scroll", check, { passive: true })
    window.addEventListener("resize", check)
    return () => {
      window.removeEventListener("scroll", check)
      window.removeEventListener("resize", check)
    }
  }, [])

  useEffect(() => {
    if (!barRef.current) return
    setBarHeight(barRef.current.offsetHeight)
  }, [open, active])

  const activeLabel =
    sections.find((s) => s.id === active)?.label || sections[0]?.label || ""

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string
  ) => {
    e.preventDefault()
    const el = document.getElementById(`${clinicId}-${id}`)
    if (!el) return
    // Close the dropdown FIRST so the bar's in-flow spacer collapses before
    // the browser computes the smooth-scroll target. Otherwise the spacer
    // shrinks mid-scroll and the destination ends up under-scrolled
    // (target section appears at the bottom of the viewport instead of the
    // top — see AHD-6179).
    setOpen(false)
    window.history.replaceState(null, "", `#${clinicId}-${id}`)
    // Two rAFs: first lets React commit the closed-dropdown render, second
    // lets the layout/spacer-height useEffect flush.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" })
      })
    })
  }

  return (
    <div className="md:hidden">
      <div ref={sentinelRef} aria-hidden="true" />
      {stuck && <div aria-hidden="true" style={{ height: barHeight }} />}
      <div
        ref={barRef}
        className={cn(
          "z-30",
          stuck ? "fixed inset-x-0 top-[68px]" : "relative -mx-6 mb-8"
        )}
      >
        <div className="bg-background border-b border-neutral-200">
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            className="flex w-full items-center justify-between px-6 py-3 text-left"
          >
            <span className="type-body-md text-foreground">{activeLabel}</span>
            <ChevronDownIcon
              className={cn(
                "text-neutral-500 transition-transform duration-200",
                open && "rotate-180"
              )}
              width={18}
              height={18}
            />
          </button>
          {open && (
            <ul className="flex flex-col border-t border-neutral-200 bg-white">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${clinicId}-${s.id}`}
                    onClick={(e) => handleLinkClick(e, s.id)}
                    className={cn(
                      "type-body-md block px-6 py-3 transition-colors",
                      active === s.id
                        ? "text-foreground font-medium"
                        : "text-neutral-500"
                    )}
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Desktop sticky sidebar ───────────────────────────────
// TODO: Componentize and share with biomarker/test pages once the design settles.

function ClinicSidebar({
  sections,
  clinicId,
  locale,
  lp,
  t,
}: {
  sections: { id: string; label: string }[]
  clinicId: string
  locale: string
  lp: (path: string) => string
  t: (key: string) => string
}) {
  const country = useCountry().code
  const [activeId, setActiveId] = useState<string | null>(
    sections[0]?.id ?? null
  )

  useEffect(() => {
    if (sections.length === 0) return
    const elements = sections
      .map((s) => document.getElementById(`${clinicId}-${s.id}`))
      .filter((el): el is HTMLElement => el !== null)
    if (elements.length === 0) return

    const visible = new Set<string>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id)
          else visible.delete(entry.target.id)
        }
        if (visible.size === 0) return
        const next = sections.find((s) => visible.has(`${clinicId}-${s.id}`))
        if (next) setActiveId(next.id)
      },
      { rootMargin: "-120px 0px -55% 0px", threshold: 0 }
    )
    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [sections, clinicId])

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const el = document.getElementById(`${clinicId}-${id}`)
    if (!el) return
    el.scrollIntoView({ behavior: "smooth", block: "start" })
    window.history.replaceState(null, "", `#${clinicId}-${id}`)
    setActiveId(id)
  }

  return (
    <div className="sticky top-24 flex flex-col gap-8">
      {sections.length > 0 && (
        <nav aria-label={t("cp_onThisPage")}>
          <h2 className="type-label-lg mb-4 text-neutral-400">
            {t("cp_onThisPage")}
          </h2>
          <ul className="flex flex-col gap-3">
            {sections.map((s) => {
              const isActive = activeId === s.id
              return (
                <li key={s.id}>
                  <a
                    href={`#${clinicId}-${s.id}`}
                    onClick={(e) => handleClick(e, s.id)}
                    aria-current={isActive ? "location" : undefined}
                    className={cn(
                      "type-body-lg transition-colors hover:text-neutral-900",
                      isActive
                        ? "font-medium text-neutral-900"
                        : "text-neutral-500"
                    )}
                  >
                    {s.label}
                  </a>
                </li>
              )
            })}
          </ul>
        </nav>
      )}
      <div className="flex flex-col gap-3">
        <a href={bookingUrlForCountry(country, locale)}>
          <Button className="w-full">{t("cp_bookCheckup")}</Button>
        </a>
        <Link href={lp("/how-it-works")}>
          <Button variant="outline" className="w-full">
            {t("cp_howItWorks")}
          </Button>
        </Link>
      </div>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────

export function ClinicDetailClient({
  city,
  citySlug,
  clinicSlug,
  clinic,
  suggestedClinics,
  initialFaqs,
}: ClinicDetailClientProps) {
  const ut = useTranslations("ui")
  const t = useTranslations("locations")
  const locale = useLocale()
  const lp = useLocalePath()
  const detail = clinic.detail
  const clinicId = clinic.id
  // CMS locale these fields were fetched in — the inline editor registers
  // regions client-side targeting this Payload locale row. No-op in public prod.
  const country = useCountry().code
  const editLocale = cmsLocale(locale, country)

  // Build anchor sections
  const navSections: { id: string; label: string }[] = []
  if (detail) {
    navSections.push({ id: "address", label: t("cp_addressAndContact") })
    if (detail.imagingServices.length > 0 || detail.bloodServices.length > 0) {
      navSections.push({
        id: "availability",
        label: t("cp_availableAtLocation"),
      })
    }
    if (detail.parking && detail.parking.length > 0)
      navSections.push({ id: "parking", label: t("cp_parkingInfo") })
    if (detail.publicTransport)
      navSections.push({ id: "transport", label: t("cp_publicTransport") })
  }

  return (
    <>
      {/* Clinic detail */}
      <section className="px-6 pt-6 pb-16 md:pt-8">
        <div className="mx-auto max-w-[1100px]">
          <div className="grid gap-10 md:grid-cols-[240px_minmax(0,1fr)] lg:gap-16">
            {/* Desktop sticky sidebar (On this page + CTAs) */}
            <aside className="hidden md:block md:pt-11">
              {navSections.length > 0 && (
                <ClinicSidebar
                  sections={navSections}
                  clinicId={clinicId}
                  locale={locale}
                  lp={lp}
                  t={t as (key: string) => string}
                />
              )}
            </aside>

            {/* Content column */}
            <article>
              {/* Breadcrumb */}
              <nav
                aria-label="Breadcrumb"
                className="type-body-md mb-6 flex items-center gap-2 text-neutral-400"
              >
                <Link
                  href={lp("/locations")}
                  className="transition-colors hover:text-neutral-700"
                >
                  {t("heroLabel")}
                </Link>
                <span aria-hidden="true">/</span>
                <Link
                  href={lp(`/locations/${citySlug}`)}
                  className="transition-colors hover:text-neutral-700"
                >
                  {city}
                </Link>
                <span aria-hidden="true">/</span>
                <span className="truncate text-neutral-600" aria-current="page">
                  {clinic.clinic}
                </span>
              </nav>

              {/* Clinic image */}
              {clinic.image && (
                <div>
                  <div className="relative mb-8 aspect-[3/1] max-h-[360px] w-full overflow-hidden rounded-[20px]">
                    <Image
                      src={clinic.image}
                      alt={clinic.clinic}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, 800px"
                      priority
                    />
                  </div>
                </div>
              )}

              {/* Hero */}
              <div>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span
                    className="inline-block rounded-full px-2.5 py-1 text-sm font-medium"
                    style={
                      clinic.type === "mri"
                        ? {
                            backgroundColor: "var(--color-light-blue-100)",
                            color: "var(--color-deep-blue-800)",
                          }
                        : {
                            backgroundColor: "#FCDFD3",
                            color: "#C84F1F",
                          }
                    }
                  >
                    {clinic.type === "mri"
                      ? t("fullBodyCheckupBadge")
                      : t("bloodTestBadge")}
                  </span>
                </div>
                <h1 className="type-display-lg text-foreground text-3xl font-normal sm:text-4xl md:leading-[1]">
                  {clinic.clinic}
                </h1>
                {detail?.description && (
                  <EditableField
                    collection="locations"
                    id={clinicId}
                    field="description"
                    locale={editLocale}
                    kind="scalar"
                    as="p"
                    className="mt-4 max-w-3xl text-base leading-relaxed text-neutral-700"
                  >
                    {detail.description}
                  </EditableField>
                )}
                {detail?.languageNote && (
                  <ClinicLanguageNote
                    clinicId={clinicId}
                    note={detail.languageNote}
                    className="mt-4"
                  />
                )}
                {/* Mobile-only hero CTAs — desktop has them in the sidebar */}
                <div className="mt-6 flex flex-wrap items-center gap-4 md:hidden">
                  <a href={bookingUrlForCountry(country, locale)}>
                    <Button>{t("cp_bookCheckup")}</Button>
                  </a>
                  <Link
                    href={lp("/how-it-works")}
                    className="type-body-md text-foreground underline decoration-neutral-300 underline-offset-4 transition-opacity hover:opacity-70"
                  >
                    {t("cp_howItWorks")}
                  </Link>
                </div>
              </div>

              {/* Mobile sticky anchor dropdown — desktop uses the sidebar */}
              {navSections.length > 0 && (
                <div className="mt-8 md:hidden">
                  <MobileSectionNav
                    sections={navSections}
                    clinicId={clinicId}
                  />
                </div>
              )}

              {/* Address and contact */}
              {detail && (
                <ClinicAddressHours
                  clinicId={clinicId}
                  address={clinic.address}
                  hours={detail.hours}
                  className="mt-10"
                />
              )}

              {/* Available at this location */}
              {detail && (
                <ClinicServices
                  clinicId={clinicId}
                  clinicType={clinic.type}
                  imagingServices={detail.imagingServices}
                  bloodServices={detail.bloodServices}
                  className="mt-12"
                />
              )}

              {/* Parking information */}
              {detail && (
                <ClinicParking
                  clinicId={clinicId}
                  parking={detail.parking}
                  className="mt-12"
                />
              )}

              {/* Public transport */}
              {detail && (
                <ClinicTransport
                  clinicId={clinicId}
                  publicTransport={detail.publicTransport}
                  className="mt-12"
                />
              )}

              {/* Map */}
              <div>
                <div className="mt-12">
                  <SingleLocationMap
                    markers={[
                      {
                        lat: clinic.lat,
                        lng: clinic.lng,
                        type: clinic.type,
                        label: clinic.clinic,
                        address: clinic.address,
                      },
                    ]}
                    center={{ lat: clinic.lat, lng: clinic.lng }}
                  />
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* FAQ — dark section */}
      {initialFaqs && initialFaqs.length > 0 && (
        // currentPage is the per-clinic path so it never matches the CMS
        // "locations" tag — schema for locations-tagged FAQs lives on the
        // /locations index, not on every clinic page (~50+ pages).
        <FaqSection
          heading={t("cp_faqTitle")}
          items={initialFaqs}
          variant="dark"
          linkLabel={null}
          currentPage={`locations/${citySlug}/${clinicSlug}`}
        />
      )}

      {/* Suggested clinics — same city first, then rest of country */}
      {suggestedClinics.length > 0 && (
        <section className="px-6 py-16" data-mobile-nav-end>
          <div className="mx-auto max-w-[1100px]">
            <div>
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <h2 className="type-title-lg text-neutral-900">
                  {t("cp_otherLocations")}
                </h2>
                <Link
                  href={lp("/locations")}
                  className="type-body-md text-foreground underline decoration-neutral-300 underline-offset-4 transition-opacity hover:opacity-70"
                >
                  {ut("viewAllLocations")}
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {suggestedClinics.map((c) => (
                <div key={c.id} className="h-full">
                  <Link
                    href={lp(`/locations/${c.citySlug}/${c.clinicSlug}`)}
                    className="group block h-full"
                  >
                    <div className="flex h-full flex-col overflow-hidden rounded-[16px] bg-white transition-shadow duration-200 group-hover:shadow-[0_0_20px_rgba(0,0,0,0.06)]">
                      {c.image && (
                        <div className="relative aspect-[16/9] w-full">
                          <Image
                            src={c.image}
                            alt={c.clinic}
                            fill
                            className="object-cover"
                            sizes="(max-width: 768px) 100vw, 33vw"
                          />
                        </div>
                      )}
                      <div className="flex flex-1 flex-col p-6">
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                          <span
                            className="inline-block rounded-full px-2 py-0.5 text-xs font-medium"
                            style={
                              c.type === "mri"
                                ? {
                                    backgroundColor:
                                      "var(--color-light-blue-50)",
                                    color: "var(--color-bright-blue-700)",
                                  }
                                : {
                                    backgroundColor: "var(--color-error-100)",
                                    color: "#E96F3F",
                                  }
                            }
                          >
                            {c.type === "mri"
                              ? t("fullBodyCheckupBadge")
                              : t("bloodTestBadge")}
                          </span>
                        </div>
                        <h3 className="text-foreground text-xl font-medium transition-opacity group-hover:opacity-70">
                          {c.clinic}
                        </h3>
                        {c.description && (
                          <EditableField
                            collection="locations"
                            id={c.id}
                            field="description"
                            locale={editLocale}
                            kind="scalar"
                            as="p"
                            className="mt-2 line-clamp-2 text-sm leading-relaxed text-neutral-700"
                          >
                            {c.description}
                          </EditableField>
                        )}
                        <p className="mt-3 flex items-center gap-1.5 text-sm text-neutral-600">
                          <MapPinIcon
                            width={14}
                            height={14}
                            className="flex-shrink-0"
                          />
                          {c.address}
                        </p>
                        <div className="mt-auto flex items-center gap-1.5 pt-8 text-sm font-medium text-neutral-700 transition-opacity group-hover:opacity-70">
                          {t("cp_viewDetails")}
                          <ChevronRightIcon width={14} height={14} />
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
            <div>
              <div className="mt-10 text-center">
                <Link href={lp("/locations")}>
                  <Button variant="outline">{ut("viewAllLocations")}</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA banner — dark section */}
      <section className="bg-foreground px-6 py-20 text-center md:py-28">
        <div>
          <h2 className="type-display-lg mx-auto max-w-2xl text-3xl font-normal text-white sm:text-4xl md:leading-[1]">
            {t("cp_readyToBook")}
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href={bookingUrlForCountry(country, locale)}>
              <Button className="text-foreground bg-white hover:bg-white/90">
                {t("cp_bookCheckup")}
              </Button>
            </a>
            <Link href={lp("/contact")}>
              <Button variant="text" className="text-white hover:text-white/80">
                {t("cp_talkToTeam")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
